import { useEffect, useState } from "react";
import { Alert } from "@narmi/design_system";
import { getInForm } from "byzantine";

export const getRecipientError = (form: any, index: number) =>
  form.submitCount > 0 && getInForm(form.errors, `payments.${index}.recipient`);

export const getAmountError = (form: any, index: number) =>
  form.submitCount > 0 && getInForm(form.errors, `payments.${index}.amount`);

export const getDateError = (form: any, index: number) =>
  form.submitCount > 0 &&
  getInForm(form.errors, `payments.${index}.scheduledDate`);

type TableErrorSummaryProps = {
  clickedReview: boolean;
  form: {
    submitCount: number;
    errors: Record<string, any>;
    values: { payments: any[] };
  };
  showDateErrors?: boolean;
  allPaymentsExcluded?: boolean;
  isImportMode?: boolean;
};

const TableErrorSummary = ({
  clickedReview,
  form,
  showDateErrors = false,
  allPaymentsExcluded = false,
  isImportMode = false,
}: TableErrorSummaryProps) => {
  const [errorMessages, setErrorMessages] = useState<string>("");
  const { errors, values } = form;
  const { payments } = values;
  const paymentsErrors = errors.payments ?? [];

  const getErrorMessages = () => {
    const messages = [];
    if (payments.length === 0 || allPaymentsExcluded) {
      messages.push("Please add at least one payment before continuing.");
    }

    let recipientError = "";
    let amountError = "";
    const dateErrorSet = new Set<string>();

    // array of payment errors with each item corresponding to a row in the table
    if (Array.isArray(paymentsErrors)) {
      paymentsErrors.forEach((error, i) => {
        if (!error || typeof error === "string") {
          return;
        }

        // as long as there is 1 recipient or 1 amount error, we show the error message
        if (error.recipient) {
          recipientError = getRecipientError(form, i);
        }

        if (error.amount) {
          amountError = getAmountError(form, i);
        }

        // there are 2 types of date errors
        if (showDateErrors && error.scheduledDate) {
          const dateErrorMessage = getDateError(form, i);
          if (dateErrorMessage) {
            dateErrorSet.add(dateErrorMessage); // dedup automatically by using set
          }
        }
      });
    }

    if (recipientError) {
      messages.push(recipientError);
    }

    if (amountError) {
      messages.push(amountError);
    }

    if (showDateErrors && dateErrorSet.size > 0) {
      messages.push(...dateErrorSet);
    }

    const formatted = messages.map((m) => `- ${m}`).join("\n");
    return formatted;
  };

  // Recompute the summary when the user clicks "Review" OR on the ACH import
  // landing path (isImportMode). On import landing PaymentsTableScreen validates
  // and bumps submitCount on mount, so the per-row error cells (gated on
  // submitCount > 0) and this banner surface the already-known row errors
  // without requiring a "Review" click.
  useEffect(() => {
    if (clickedReview || isImportMode) {
      const message = getErrorMessages();
      setErrorMessages(message);
    }
  }, [
    payments,
    paymentsErrors,
    clickedReview,
    form.submitCount,
    showDateErrors,
    allPaymentsExcluded,
    isImportMode,
  ]);

  if (paymentsErrors.length === 0 && !allPaymentsExcluded) return null;

  return (
    <div className={errorMessages.length ? "margin--top--xl" : ""}>
      <Alert
        isActive={Boolean(errorMessages)}
        kind="error"
        isDismissable={false}
      >
        Please review the error(s) before continuing:
        <div style={{ whiteSpace: "pre-wrap" }}>{errorMessages}</div>
      </Alert>
    </div>
  );
};

export default TableErrorSummary;
